import { useCallback, useEffect, useRef, useState } from 'react';
import { useSnapshot } from 'valtio';

import { APOLLO_CLIENTS, CustomContext } from '@/graphql/client';
import { useOfflinePaymentAvailabilityLazyQuery } from '@/graphql/orderApp/api';
import type { PaymentGatewayConfig } from '@/graphql/saleor/api';
import { usePaymentGatewayInitializeMutation } from '@/graphql/saleor/api';
import {
  buildPaymentGatewayCacheKey,
  isPaymentGatewayCacheValid,
  loadOfflinePaymentAvailability,
  startPaymentGatewayFetch,
} from '@/hooks/paymentGatewayInitializeCoordinator';
import { PAYMENT_APPS_ID } from '@/lib/const';
import type { MulticardConfig } from '@/lib/payment/types';
import { PAYMENT_TYPES } from '@/lib/payment/types';
import { useRegionProvider } from '@/providers/RegionProvider';
import {
  paymentGatewaysActions,
  paymentGatewaysState,
} from '@/states/paymentGatewaysState';

const EMPTY_GATEWAY_CONFIGS: typeof paymentGatewaysState.configs = [];

const resolveVisibleCacheKey = (
  cacheKey: string | null,
  hasCurrentEntry: boolean,
  previousKey: string | null,
): string | null => {
  if (!cacheKey) {
    return null;
  }
  if (hasCurrentEntry) {
    return cacheKey;
  }
  return previousKey;
};

export const useGetPaymentGateways = (
  orderOrCheckoutId?: string,
  amount?: number,
  billingAddressCountryCode?: string,
) => {
  const { country, locale } = useRegionProvider();
  const [mutation] = usePaymentGatewayInitializeMutation();
  const [queryOfflinePaymentAvailability] =
    useOfflinePaymentAvailabilityLazyQuery({
      context: CustomContext({
        disableErrorNotification: true,
        client: APOLLO_CLIENTS.orderApp,
      }),
      fetchPolicy: 'no-cache',
    });
  const countryCode = billingAddressCountryCode || country.code;
  const shopperLocale = locale.locale;
  const cacheKey =
    orderOrCheckoutId && amount
      ? buildPaymentGatewayCacheKey(
          orderOrCheckoutId,
          amount,
          countryCode,
          shopperLocale,
        )
      : null;
  const hasFreshCache =
    !!cacheKey &&
    !!orderOrCheckoutId &&
    amount !== undefined &&
    isPaymentGatewayCacheValid(cacheKey, orderOrCheckoutId, amount);
  const [loading, setLoading] = useState(!hasFreshCache);
  const [hasFetched, setHasFetched] = useState(hasFreshCache);
  const [hasFoundPaymentMethods, setHasFoundPaymentMethods] =
    useState(hasFreshCache);
  const paymentGatewaysSnap = useSnapshot(paymentGatewaysState);
  const resolvedKeyRef = useRef<string | null>(hasFreshCache ? cacheKey : null);

  if (cacheKey && paymentGatewaysSnap.entries[cacheKey]) {
    resolvedKeyRef.current = cacheKey;
  }

  const visibleKey = resolveVisibleCacheKey(
    cacheKey,
    Boolean(cacheKey && paymentGatewaysSnap.entries[cacheKey]),
    resolvedKeyRef.current,
  );
  const visibleEntry = visibleKey
    ? paymentGatewaysSnap.entries[visibleKey]
    : undefined;

  const shouldSkipFetch = useCallback(() => {
    return !orderOrCheckoutId || amount === undefined || amount === 0;
  }, [orderOrCheckoutId, amount]);

  const parseGatewayConfigs = useCallback((configs: PaymentGatewayConfig[]) => {
    let multicardConfig: MulticardConfig | null = null;

    const gatewayConfigs = configs.map(({ data, id }) => {
      const paymentMethods = data?.paymentMethods?.map(
        ({
          name,
          type,
          ...dropInConfiguration
        }: Record<string, string | string[]>) => {
          return {
            name,
            type,
            dropInConfiguration,
          };
        },
      );

      if (data?.multicardMaxPayments) {
        multicardConfig = {
          paidMoney: data?.paidMoney,
          maxPayments: data?.multicardMaxPayments,
          maxAmount: data?.nextPaymentMaxAmount,
          minAmount: data?.nextPaymentMinAmount,
        } as MulticardConfig;
      }

      return { id, paymentMethods };
    });

    return { gatewayConfigs, multicardConfig };
  }, []);

  const syncFromSharedState = useCallback(() => {
    const entry = cacheKey ? paymentGatewaysState.entries[cacheKey] : undefined;
    setHasFetched(true);
    setLoading(false);
    setHasFoundPaymentMethods((entry?.configs.length ?? 0) > 0);
  }, [cacheKey]);

  const fetchPaymentGateways = useCallback(
    async (forceRefresh = false) => {
      if (shouldSkipFetch()) {
        setLoading(false);
        return;
      }

      const nextCacheKey = buildPaymentGatewayCacheKey(
        orderOrCheckoutId!,
        amount!,
        countryCode,
        shopperLocale,
      );

      if (
        !forceRefresh &&
        isPaymentGatewayCacheValid(nextCacheKey, orderOrCheckoutId!, amount!)
      ) {
        syncFromSharedState();
        return;
      }

      const runFetch = async (
        isCurrent: () => boolean,
        isLatest: () => boolean,
      ) => {
        if (!isCurrent()) return;

        const [{ data }, isOfflinePaymentAvailable] = await Promise.all([
          mutation({
            variables: {
              id: orderOrCheckoutId!,
              amount,
              paymentGateways: PAYMENT_APPS_ID.map((id) => ({
                id,
                data: {
                  countryCode,
                  shopperLocale,
                },
              })),
            },
            context: CustomContext({
              disableErrorNotification: true,
            }),
          }),
          loadOfflinePaymentAvailability(
            orderOrCheckoutId!,
            amount!,
            async () => {
              const { data: offlineData, error } =
                await queryOfflinePaymentAvailability({
                  variables: {
                    id: orderOrCheckoutId!,
                  },
                });
              return {
                available: !!offlineData?.storefrontOfflinePaymentAvailability,
                failed: !!error,
              };
            },
          ),
        ]);

        if (!isCurrent()) return;

        const configs =
          data?.paymentGatewayInitialize?.gatewayConfigs?.filter(
            ({ data: gatewayData }) => !!gatewayData?.paymentMethods,
          ) ?? [];

        const { gatewayConfigs, multicardConfig } =
          parseGatewayConfigs(configs);

        if (isOfflinePaymentAvailable) {
          gatewayConfigs.push({
            id: PAYMENT_TYPES.OFFLINE,
            paymentMethods: [
              {
                type: PAYMENT_TYPES.OFFLINE,
              },
            ],
          });
        }

        if (!isCurrent()) return;

        paymentGatewaysActions.setEntry(nextCacheKey, {
          id: orderOrCheckoutId!,
          amount: amount!,
          configs: gatewayConfigs,
          multicardConfig,
        });

        if (!isLatest()) return;

        const keepPartialAmount =
          orderOrCheckoutId !== paymentGatewaysState.id &&
          amount === paymentGatewaysState.amount;
        paymentGatewaysActions.clear(keepPartialAmount);
        if (multicardConfig) {
          paymentGatewaysActions.setMulticardConfig(multicardConfig);
        }
        paymentGatewaysActions.set(orderOrCheckoutId!, amount!, gatewayConfigs);
      };

      const ownEntry = paymentGatewaysState.entries[nextCacheKey];
      const retainedEntry = resolvedKeyRef.current
        ? paymentGatewaysState.entries[resolvedKeyRef.current]
        : undefined;
      if (
        (ownEntry?.configs.length ?? 0) === 0 &&
        (retainedEntry?.configs.length ?? 0) === 0
      ) {
        setLoading(true);
      }
      try {
        const stillCurrent = await startPaymentGatewayFetch(
          nextCacheKey,
          runFetch,
        );
        if (stillCurrent) {
          syncFromSharedState();
        }
      } catch (error) {
        console.error('Error fetching payment gateways:', error);
        if (
          (paymentGatewaysState.entries[nextCacheKey]?.configs.length ?? 0) ===
          0
        ) {
          syncFromSharedState();
        }
      }
    },
    [
      mutation,
      orderOrCheckoutId,
      amount,
      countryCode,
      shopperLocale,
      parseGatewayConfigs,
      queryOfflinePaymentAvailability,
      shouldSkipFetch,
      syncFromSharedState,
    ],
  );

  useEffect(() => {
    fetchPaymentGateways();
  }, [fetchPaymentGateways]);

  return {
    isLoading: loading,
    hasFetched,
    hasFoundPaymentMethods,
    gatewayConfigs: visibleEntry?.configs ?? EMPTY_GATEWAY_CONFIGS,
    multicardConfig: visibleEntry?.multicardConfig ?? undefined,
    partialAmount: paymentGatewaysSnap.partialAmount,
    fetchPaymentGateways,
  };
};
